'use client';

import type { ComponentProps } from 'react';
import Image from 'next/image';
import imageUrlBuidler from '@sanity/image-url';
import type {
  SanityAsset,
  SanityImageObject,
  SanityImageWithAssetStub,
  SanityReference,
} from '@sanity/image-url/lib/types/types';

/**
 * Generate an image URL builder for Sanity images.
 */

const projectId = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID!;
const dataset = process.env.NEXT_PUBLIC_SANITY_DATASET!;

const imageBuilder = imageUrlBuidler({
  projectId,
  dataset,
});

export type SanityImageSource = Parameters<typeof imageBuilder.image>[0];

export type SourceWithMetadata = SanityImageWithAssetStub & {
  asset: {
    url: string;
    metadata: {
      lqip: string;
    };
  };
};

export function sanityImageUrl(source: SanityImageSource) {
  return imageBuilder.image(source);
}

export function getSanityRefId(image: SanityImageSource): string {
  if (typeof image === 'string') {
    return image;
  }

  const obj = image as SanityImageObject;
  const ref = image as SanityReference;
  const img = image as SanityAsset;

  if (obj.asset) {
    return obj.asset._ref || (obj.asset as SanityAsset)._id;
  }

  return ref._ref || img._id || '';
}

export function getImageDimensions(id: string) {
  const dimensions = id.split('-')[2];

  const [width, height] = dimensions
    .split('x')
    .map((num: string) => parseInt(num, 10));
  const aspectRatio = width / height;

  return { width, height, aspectRatio };
}

export type UseSanityImageProps = {
  src: SanityImageSource | SourceWithMetadata;
  /** width as an optional prop */
  width?: number;
  /** height as an optional prop */
  height?: number;
  /** provide an aspect ratio */
  aspectRatio?: Array<number>;
};

export function useSanityImage({
  src,
  width: widthProp,
  height: heightProp,
  aspectRatio: aspectRatioProp,
}: UseSanityImageProps) {
  const id = getSanityRefId(src);
  const dimensions = getImageDimensions(id);
  const imageSource = sanityImageUrl(src).url();

  // determine if we have a lqip to use as a placeholder
  const lqip = (src as SourceWithMetadata)?.asset.metadata?.lqip;

  const defaultWidth = dimensions.width;
  const defaultHeight = dimensions.height;

  let width = widthProp;

  if (!width && heightProp) {
    if (aspectRatioProp) {
      width = (heightProp / aspectRatioProp[1]) * aspectRatioProp[0];
    } else {
      width = (defaultWidth / defaultHeight) * heightProp;
    }
  }

  if (!width) {
    width = defaultWidth;
  }

  let height = heightProp;

  if (widthProp && !height) {
    if (aspectRatioProp) {
      height = (widthProp / aspectRatioProp[0]) * aspectRatioProp[1];
    } else {
      height = (defaultHeight / defaultWidth) * widthProp;
    }
  }

  if (!height) {
    height = defaultHeight;
  }

  const loader: ComponentProps<typeof Image>['loader'] = ({
    width,
    quality,
  }) => {
    return sanityImageUrl(src)
      .auto('format')
      .width(width)
      .quality(quality ?? 100)
      .fit('max')
      .url();
  };

  return {
    defaultWidth,
    defaultHeight,
    height,
    imageSource,
    loader,
    lqip,
    width,
  };
}
